/** The sound drop-down as TradingView's alert window has it: a button with the sound's name; opened, a
 * list of groups (Classic, Soft, Nature, Voices, Funny, then any group of the server's files), one open
 * at a time, each sound a row with a play button. The chosen sound is the dark row. A click on a row
 * chooses it (and plays it); a click on its play button only plays it. */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type RefObject,
} from "react";
import { MUTE_CHIME, playChime, unlockAudio } from "@/lib/notify";
import { FILE_PREFIX, SOUND_GROUPS, soundsOf, type SoundGroup } from "@/lib/soundLibrary";
import { groupOf, soundName } from "@/lib/soundNames";
import type { ServerSound } from "@/lib/soundsApi";

interface Row {
  id: string;
  label: string;
}

function titled(group: string): string {
  return group.charAt(0).toUpperCase() + group.slice(1);
}

function isGroup(group: string): group is SoundGroup {
  return (SOUND_GROUPS as readonly string[]).includes(group);
}

/** The groups to list: the five, then each other group the published files are in. */
function groupsOf(files: readonly ServerSound[]): string[] {
  return [...new Set<string>([...SOUND_GROUPS, ...files.map((f) => titled(f.group))])];
}

function rowsOf(group: string, files: readonly ServerSound[]): Row[] {
  const own = isGroup(group) ? soundsOf(group).map((s) => ({ id: s.id, label: s.label })) : [];
  const mine = files
    .filter((f) => titled(f.group) === group)
    .map((f) => ({ id: `${FILE_PREFIX}${f.id}`, label: `${f.label} (file)` }));
  return [...own, ...mine];
}

function play(id: string): void {
  unlockAudio(); // inside the click, so the browser allows sound
  playChime("entry", id);
}

function SoundRow({
  row,
  chosen,
  onChoose,
}: Readonly<{ row: Row; chosen: boolean; onChoose: (id: string) => void }>): ReactElement {
  return (
    <div
      role="option"
      aria-selected={chosen}
      className={`flex items-center justify-between rounded-ctl px-3 py-2 text-sm ${
        chosen ? "bg-ink text-surface-1" : "hover:bg-surface-2"
      }`}
    >
      <button
        type="button"
        className="flex-1 text-left"
        onClick={() => {
          onChoose(row.id);
        }}
      >
        {row.label}
      </button>
      <button
        type="button"
        aria-label={`Play ${row.label}`}
        className="ml-2"
        onClick={() => {
          play(row.id);
        }}
      >
        <i className="ti ti-player-play text-base" />
      </button>
    </div>
  );
}

/** Where the list is drawn: relative to the page, so no window around the picker can cut it off. */
type Place = CSSProperties;

const LIST_HEIGHT = 340;
const MIN_LIST = 120;
const MARGIN = 8;
const GAP = 4;
const LIST_WIDTH = 288;

/** Under the button or above it, wherever there is more room, as tall as the room allows, scrolling inside. */
function placeOf(box: DOMRect): Place {
  const below = window.innerHeight - box.bottom - MARGIN;
  const above = box.top - MARGIN;
  const up = below < Math.min(LIST_HEIGHT, above) && above > below;
  const width = Math.max(box.width, LIST_WIDTH);
  return {
    position: "fixed",
    width,
    left: Math.max(MARGIN, Math.min(box.left, window.innerWidth - width - MARGIN)),
    ...(up ? { bottom: window.innerHeight - box.top + GAP } : { top: box.bottom + GAP }),
    maxHeight: Math.max(MIN_LIST, Math.min(LIST_HEIGHT, up ? above : below)),
  };
}

function Groups({
  value,
  files,
  place,
  onChoose,
}: Readonly<{
  value: string;
  files: readonly ServerSound[];
  place: Place;
  onChoose: (id: string) => void;
}>): ReactElement {
  const [open, setOpen] = useState<string | null>(groupOf(value, files));
  return (
    <div
      role="listbox"
      style={place}
      className="z-50 overflow-y-auto overscroll-contain rounded-ctl border border-line bg-surface-1 p-1 shadow-lg"
    >
      {groupsOf(files).map((group) => (
        <div key={group} className="border-b border-line last:border-b-0">
          <button
            type="button"
            aria-expanded={open === group}
            className="flex w-full items-center justify-between px-3 py-2 text-[11px] uppercase tracking-wide text-ink-muted"
            onClick={() => {
              setOpen(open === group ? null : group);
            }}
          >
            {group}
            <i className={`ti ${open === group ? "ti-chevron-up" : "ti-chevron-down"} text-base`} />
          </button>
          {open === group &&
            rowsOf(group, files).map((row) => (
              <SoundRow key={row.id} row={row} chosen={row.id === value} onChoose={onChoose} />
            ))}
        </div>
      ))}
      <SoundRow
        row={{ id: MUTE_CHIME, label: "Mute" }}
        chosen={value === MUTE_CHIME}
        onChoose={onChoose}
      />
    </div>
  );
}

/** Close the list on a press outside it, and on Escape. */
function useDismiss(open: boolean, close: () => void, box: RefObject<HTMLDivElement | null>): void {
  useEffect(() => {
    if (!open) return undefined;
    const outside = (event: MouseEvent): void => {
      if (box.current !== null && !box.current.contains(event.target as Node)) close();
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") close();
    };
    const behind = (event: Event): void => {
      if (box.current !== null && !box.current.contains(event.target as Node)) close(); // not the list's own scrolling
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    window.addEventListener("resize", close);
    document.addEventListener("scroll", behind, true);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
      window.removeEventListener("resize", close);
      document.removeEventListener("scroll", behind, true);
    };
  }, [open, close, box]);
}

function Trigger({
  label,
  text,
  open,
  onClick,
}: Readonly<{ label: string; text: string; open: boolean; onClick: () => void }>): ReactElement {
  return (
    <button
      type="button"
      className="input flex w-full items-center justify-between text-left"
      aria-label={label}
      aria-haspopup="listbox"
      aria-expanded={open}
      onClick={onClick}
    >
      {text}
      <i className={`ti ${open ? "ti-chevron-up" : "ti-chevron-down"} text-base`} />
    </button>
  );
}

export function SoundPicker({
  label,
  value,
  files,
  onChange,
}: Readonly<{
  label: string;
  value: string;
  /** the published files of the server's folder */
  files: readonly ServerSound[];
  onChange: (id: string) => void;
}>): ReactElement {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<Place>({});
  const box = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useDismiss(open, close, box);
  const toggle = (): void => {
    if (!open && box.current !== null) setPlace(placeOf(box.current.getBoundingClientRect()));
    setOpen(!open);
  };
  return (
    <div ref={box} className="relative">
      <Trigger label={label} text={soundName(value, files)} open={open} onClick={toggle} />
      {open && (
        <Groups
          value={value}
          files={files}
          place={place}
          onChoose={(id) => {
            onChange(id);
            setOpen(false);
            play(id);
          }}
        />
      )}
    </div>
  );
}
